import {
    AccordionDropdownMultiSelectList,
    DropdownButtonTextRowMultiSelect,
} from "./DropdownMultiSelect.styles";
import {
    AccordionIcon,
    AccordionTitle,
} from "shared/components/data-display/accordion/Accordion.styles";
import {
    DropdownButton,
    DropdownButtonIcon,
    DropdownButtonText,
    DropdownButtonTextRow,
    DropdownButtonWrapper,
    DropdownWrapper,
} from "shared/components/inputs/dropdown/Dropdown.styles";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useCombobox, useMultipleSelection } from "downshift";

import DropdownMultiSelectList from "shared/components/inputs/dropdown-multi-select/DropdownMultiSelectList";
import HStack from "shared/components/layout/stack/HStack";
import PropTypes from "prop-types";
import Tag from "shared/components/data-display/badge/Tag";
import useDropdownListOptions from "shared/components/inputs/dropdown/useDropdownListOptions";

export { AccordionDropdownMultiSelectList, DropdownButtonTextRowMultiSelect } from "./DropdownMultiSelect.styles";

export const getDropdownMultiSelectOptions = (
    optionDetails = [],
    Id = "Id",
    Name = "Name"
) => {
    return optionDetails.reduce((acc, val) => {
        return [
            ...acc,
            {
                value: val[Id],
                label: val[Name],
                id: val[Id],
            },
        ];
    }, []);
};

// THIS COMPONENT SHOULD BE REPLACED. It's too complicated. It's mainly used in not-released admin pages.
const DropdownMultiSelect = ({
    onChangeDropdown = () => void 0,
    optionsData = [],
    initialData = [],
    isVariantToggle = false,
    disabled = false,
    isSmallSize = false,
    itemsAreLoading = false,
    feedbackType,
    isAccordion = false,
    isClearAllSelectedValue = false,
    isLabeledInTag = true,
    showCountWithLabel = true,
    title,
    ...rest
}) => {
    const [optionsList, setOptionsList] = useState(optionsData);
    const initialOptionsList = useRef([]);
    const [initialLoad, setInitialLoad] = useState(true);

    const handleInitialLoadValue = useCallback(() => {
        if (initialLoad) {
            setInitialLoad(false);
        }
    }, [initialLoad]);

    useEffect(() => {
        setOptionsList(optionsData);
    }, [optionsData]);

    // set index for accessing the list items
    useEffect(() => {
        optionsList.forEach((optionData, index) => {
            optionsList[index] = { ...optionData, parentOptionIndex: index };

            optionData?.children?.forEach((childData, childIndex) => {
                optionsList[index].children[childIndex] = {
                    ...childData,
                    childOptionIndex: `${index}_${childIndex}`,
                };
            });
        });
    }, [itemsAreLoading, optionsList]);

    const {
        filteredOptions,
        filterValue,
        setFilterValue,
        internalFilterUIShown,
    } = useDropdownListOptions(optionsList);

    useEffect(() => {
        initialData?.forEach((initialDataObj) => {
            if (initialDataObj) {
                const isParent = initialDataObj?.hasOwnProperty("children");
                const isChild = initialDataObj?.hasOwnProperty("parentId");

                switch (true) {
                    case isParent: {
                        const parentObj = optionsList.find(
                            (fp) => fp.id === initialDataObj.id
                        );
                        parentObj.check = true;
                        if (parentObj.hasOwnProperty("children")) {
                            parentObj.children.forEach((c) => (c.check = true));
                        }
                        initialOptionsList?.current.push(parentObj);
                        break;
                    }

                    case isChild: {
                        const parentObjContainD = optionsList.find(
                            (fp) => fp.id === initialDataObj.parentId
                        );
                        parentObjContainD.indeterminate = true;
                        const childObj = parentObjContainD.children.find(
                            (fpc) => fpc.id === initialDataObj.id
                        );
                        childObj.check = true;
                        initialOptionsList?.current.push(childObj);
                        break;
                    }

                    default: {
                        const singleObj = optionsList.find(
                            (fs) => fs.id === initialDataObj.id
                        );
                        if (singleObj) {
                            singleObj.check = true;
                            initialOptionsList?.current.push(singleObj);
                        }
                        break;
                    }
                }
            }
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- optionsList intentionally excluded: adding it would trigger re-runs on every mutation within this effect
    }, [initialData]);

    const {
        getSelectedItemProps,
        getDropdownProps,
        selectedItems,
        addSelectedItem,
        removeSelectedItem,
    } = useMultipleSelection({
        initialSelectedItems: initialOptionsList?.current,
    });

    useEffect(() => {
        /* remove duplicate objects which is adding by library add event when clicked on parent elements */
        const duplicateData = selectedItems.filter(
            (val, index, arr) => arr.indexOf(val) !== index
        );
        duplicateData.forEach((d) => removeSelectedItem(d));

        selectedItems.forEach((data) => {
            if (!data.check && data.indeterminate) {
                removeSelectedItem(data);
            }
        });
    }, [selectedItems, removeSelectedItem]);

    useEffect(() => {
        initialData?.length > 0 && handleInitialLoadValue();
    }, [initialData, handleInitialLoadValue]);

    const deselectAllOptions = () => {
        handleInitialLoadValue();
        const clearAllSelection =
            selectedItems.length > 0 ? selectedItems : optionsData;

        clearAllSelection.forEach((data) => {
            data.check = false;
            if (data.hasOwnProperty("children")) {
                data.indeterminate = false;
                data?.children?.forEach((childData) => (childData.check = false));
            }
            if (data.hasOwnProperty("parentId")) {
                let parentElement = optionsList.find(
                    (f) => f.id === data.parentId
                );
                parentElement.indeterminate = false;
            }
            removeSelectedItem(data);
        });
    };

    const handleOnSelectedItemChangeParentCheckbox = (
        selectedItem,
        parentObj
    ) => {
        const selectedItemHasParentId = selectedItem.hasOwnProperty("parentId");
        let allChildrenCheckboxFalse =
            selectedItemHasParentId &&
            parentObj?.children.filter((d) => d.check === false)?.length ===
            parentObj?.children.length;

        if (allChildrenCheckboxFalse) {
            parentObj.check = false;
            parentObj.indeterminate = false;
            removeSelectedItem(parentObj);
        }
        if (allChildrenCheckboxFalse && parentObj.children) {
            parentObj.children.forEach((data) => {
                data.check = false;
                removeSelectedItem(data);
            });
        }

        let checkAllChildrenCheckboxTrue =
            selectedItemHasParentId &&
            parentObj?.children.filter((d) => d.check === true)?.length ===
            parentObj?.children.length;

        if (checkAllChildrenCheckboxTrue) {
            parentObj.check = true;
            parentObj.indeterminate = false;
            addSelectedItem(parentObj);
        }
        if (checkAllChildrenCheckboxTrue && parentObj.children) {
            parentObj.children.forEach((data) => {
                data.check = true;
                removeSelectedItem(data);
            });
        }
    };

    const handleOnSelectedItemChange = (selectedItem) => {
        let parenIndex = null;
        let childIndex = null;
        let obj = null;

        handleInitialLoadValue();
        const selectedItemHasParentId = selectedItem.hasOwnProperty("parentId");
        if (selectedItemHasParentId) {
            parenIndex = optionsList.findIndex(
                (d) => d.id === selectedItem.parentId
            );
            childIndex = optionsList[parenIndex]?.children.findIndex(
                (d) => d.id === selectedItem.id
            );

            obj = optionsList[parenIndex].children[childIndex];
            optionsList[parenIndex].check = false;
            optionsList[parenIndex].indeterminate = true;
        }

        let parentObj = optionsList[parenIndex];

        switch (true) {
            case !selectedItemHasParentId &&
                !selectedItem.check &&
                !selectedItems.some((d) => d.id === selectedItem.id):
                addSelectedItem(selectedItem);
                selectedItem.check = true;
                selectedItem.indeterminate = false;

                if (selectedItem.children) {
                    selectedItem.children.forEach((data) => {
                        data.check = true;
                        removeSelectedItem(data);
                    });
                }
                break;

            case !selectedItemHasParentId && selectedItem.check:
                removeSelectedItem(selectedItem);
                selectedItem.check = false;
                selectedItem.indeterminate = false;
                if (selectedItem.children) {
                    selectedItem.children.forEach((data) => {
                        data.check = false;
                        removeSelectedItem(data);
                    });
                }
                break;

            case selectedItemHasParentId && obj.check: {
                removeSelectedItem(selectedItem);
                obj.check = false;
                const trueDataArr = parentObj?.children?.filter(
                    (d) => d.check === true
                );
                if (trueDataArr?.length !== parentObj?.children?.length) {
                    parentObj.check = false;
                    parentObj.indeterminate = true;
                    removeSelectedItem(parentObj);
                    trueDataArr.forEach((data) => {
                        addSelectedItem(data);
                    });
                }
                break;
            }

            case selectedItemHasParentId &&
                !selectedItems.some((d) => d.id === selectedItem.id):
                addSelectedItem(selectedItem);
                obj.check = true;
                break;

            default:
                break;
        }

        selectedItemHasParentId &&
            handleOnSelectedItemChangeParentCheckbox(selectedItem, parentObj);
    };

    const {
        isOpen,
        getToggleButtonProps,
        getLabelProps,
        getMenuProps,
        getInputProps,
        getComboboxProps,
        highlightedIndex,
        getItemProps,
        selectItem,
        openMenu,
    } = useCombobox({
        inputValue: "",
        selectedItem: null,
        items: optionsList,
        stateReducer: (state, actionAndChanges) => {
            const { changes, type } = actionAndChanges;
            switch (type) {
                case useCombobox.stateChangeTypes.InputKeyDownEnter:
                case useCombobox.stateChangeTypes.ItemClick:
                    return {
                        ...changes,
                        isOpen: true, // keep the menu open after selection.
                        highlightedIndex: state.highlightedIndex,
                    };
                default:
                    return changes;
            }
        },

        onSelectedItemChange: ({ type, selectedItem }) => {
            if (
                type === useCombobox.stateChangeTypes.ItemClick ||
                type === useCombobox.stateChangeTypes.FunctionSelectItem
            ) {
                handleOnSelectedItemChange(selectedItem);
            }
        },
        onStateChange: ({ inputValue, type, selectedItem }) => {
            switch (type) {
                case useCombobox.stateChangeTypes.InputChange:
                    setFilterValue(inputValue);
                    break;
                case useCombobox.stateChangeTypes.ToggleButtonClick:
                    handleInitialLoadValue();
                    break;
                case useCombobox.stateChangeTypes.InputKeyDownEnter:
                case useCombobox.stateChangeTypes.InputBlur:
                    if (selectedItem) {
                        setFilterValue("");
                    }
                    break;
                default:
                    break;
            }
        },
    });

    useEffect(() => {
        !initialLoad && onChangeDropdown(selectedItems);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- onChangeDropdown intentionally excluded; parent should wrap it in useCallback to avoid re-renders
    }, [selectedItems, initialLoad]);

    useEffect(() => {
        if (disabled && isOpen) {
            openMenu();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- isOpen and openMenu intentionally excluded; this effect should only run when disabled changes
    }, [disabled]);

    useEffect(() => {
        if (isClearAllSelectedValue) {
            deselectAllOptions();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- deselectAllOptions intentionally excluded; adding it would cause unwanted re-runs on every selectedItems/optionsList change
    }, [isClearAllSelectedValue]);

    const onClickClearSearchFieldAndResetList = () => {
        setFilterValue("");
    };

    const selectedOptionsLength = selectedItems.reduce((total, element) => {
        const len = element.children ? element.children.length : 1;
        return total + len;
    }, 0);

    let tagCountLabel = "";
    if (showCountWithLabel) {
        tagCountLabel = selectedOptionsLength > 1 ? "Options" : "Option";
    }


    return (
        <DropdownWrapper isAccordion={isAccordion}>
                <DropdownButtonWrapper
                    isDisabled={disabled}
                    {...getComboboxProps()}
                >
                    <DropdownButton
                        feedbackType={feedbackType}
                        isDisabled={disabled}
                        type="button"
                        {...getToggleButtonProps({
                            onClick: onClickClearSearchFieldAndResetList,
                        })}
                        aria-label={"toggle menu"}
                        isAccordion={isAccordion}
                        isSmallSize={isSmallSize}
                        {...rest}
                    >
                        <DropdownButtonTextRow
                            justify="space-between"
                            {...getInputProps()}
                        >
                            {selectedOptionsLength <= 0 && !isAccordion && (
                                <DropdownButtonText
                                    variant="Body_1_1"
                                    color="Gray_2"
                                >
                                    Select
                                </DropdownButtonText>
                            )}
                            {isAccordion && (
                                <AccordionTitle
                                    variant="Headline_5"
                                    show={isOpen}
                                >
                                    {title}
                                </AccordionTitle>
                            )}
                            <DropdownButtonTextRowMultiSelect
                                spacing="Zero_25"
                                {...getDropdownProps()}
                            >
                                {selectedOptionsLength !== 0 &&
                                    !isSmallSize &&
                                    selectedItems.map((selectedItem, index) => {
                                        const tagLabel = isLabeledInTag ? selectedItem.label : selectedItem.value;
                                        const parentLabel = optionsList[optionsList.findIndex((i) => i.id === selectedItem.parentId)]?.label;
                                        const label = selectedItem.hasOwnProperty("parentId")
                                            ? `${parentLabel}: ${tagLabel}`
                                            : tagLabel;
                                        return (
                                        <HStack
                                            key={selectedItem.id}
                                            {...getSelectedItemProps({
                                                selectedItem,
                                                index,
                                            })}
                                            spacing="Zero"
                                        >
                                            <Tag
                                                label={label}
                                                handleButtonOnClick={(e) => {
                                                    e.stopPropagation();
                                                    handleOnSelectedItemChange(
                                                        selectedItem
                                                    );
                                                }}
                                                onClick={(e) =>
                                                    e.stopPropagation()
                                                }
                                            />
                                        </HStack>
                                        );
                                    })}
                                {selectedOptionsLength !== 0 && isSmallSize && (
                                    <Tag
                                        label={` ${selectedOptionsLength} ${tagCountLabel} `}
                                        handleButtonOnClick={(e) => {
                                            e.stopPropagation();
                                            deselectAllOptions();
                                        }}
                                    />
                                )}
                                {isAccordion && (
                                    <AccordionIcon
                                        name={
                                            isOpen ? "caret-up" : "caret-down"
                                        }
                                        show={isOpen}
                                    />
                                )}
                            </DropdownButtonTextRowMultiSelect>
                            {!isAccordion && (
                                <DropdownButtonIcon
                                    name="caret-down"
                                    color="Gray_2"
                                    style={{ flexShrink: 0 }}
                                />
                            )}
                        </DropdownButtonTextRow>
                    </DropdownButton>
                </DropdownButtonWrapper>

                {isAccordion && <AccordionDropdownMultiSelectList />}
                <DropdownMultiSelectList
                    isBorder={!isAccordion}
                    isVariantToggle={isVariantToggle}
                    isOpen={isOpen}
                    optionsList={filteredOptions}
                    optionsAreLoading={itemsAreLoading}
                    filterValue={filterValue}
                    setFilterValue={setFilterValue}
                    internalFilterUIShown={internalFilterUIShown}
                    getLabelProps={getLabelProps}
                    getMenuProps={getMenuProps}
                    getInputProps={getInputProps}
                    getDropdownProps={getDropdownProps}
                    highlightedIndex={highlightedIndex}
                    getItemProps={getItemProps}
                    selectItem={selectItem}
                    onClickClearSearchFieldAndResetList={
                        onClickClearSearchFieldAndResetList
                    }
                />
            </DropdownWrapper>
    );
};

DropdownMultiSelect.propTypes = {
    onChangeDropdown: PropTypes.func,
    optionsData: PropTypes.array,
    initialData: PropTypes.array,
    isVariantToggle: PropTypes.bool,
    disabled: PropTypes.bool,
    isSmallSize: PropTypes.bool,
    itemsAreLoading: PropTypes.bool,
    feedbackType: PropTypes.string,
    isAccordion: PropTypes.bool,
    isClearAllSelectedValue: PropTypes.bool,
    isLabeledInTag: PropTypes.bool,
    showCountWithLabel: PropTypes.bool,
    title: PropTypes.node,
};

export default DropdownMultiSelect;
